import { useState, createContext, useContext, useEffect } from "react";
import {
  AirPlaneIcon,
  BoxIcon,
  CheckIcon,
  ClockIcon,
  CrossIcon,
  MapPinIcon,
  ShipIcon,
  SpinnerIcon,
} from "../../Icons";
import { useForm, useFieldArray } from "react-hook-form";
import { parseServerDate, timeAgo, today } from "../../../utils";
import { LogisticsRequestQuote } from "../../../types/PartnerCompanyDetails";
import { useUpdateRFQsMutation } from "../../../store/apiSlices/adminApi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { OrderRequestKeys } from "../../Dashboard/ExpandedTabs/RFQs";

type RFQContextType = {
  quote: LogisticsRequestQuote;
};

const RFQCardContext = createContext<RFQContextType | null>(null);

function useRFQCard() {
  const ctx = useContext(RFQCardContext);
  if (!ctx) {
    throw new Error("RFQCard components must be used inside <RFQCard />");
  }
  return ctx;
}

export function RFQCard({
  children,
  quote,
}: {
  children: React.ReactNode;
  quote: LogisticsRequestQuote;
}) {
  return (
    <RFQCardContext.Provider value={{ quote }}>
      <div className="p-4 bg-white border border-gray-100 rounded-xl transition-all duration-200 hover:border-gray-300">
        {children}
      </div>
    </RFQCardContext.Provider>
  );
}

export function getRFQStatusStyles(status?: LogisticsRequestQuote["status"]) {
  switch (status) {
    case "active":
      return {
        label: "Active",
        className: "bg-blue-100 text-blue-700",
      };

    case "confirmed":
      return {
        label: "Confirmed",
        className: "bg-green-100 text-green-700",
      };

    case "completed":
      return {
        label: "Completed",
        className: "bg-gray-200 text-gray-700",
      };

    case "denied":
      return {
        label: "Denied",
        className: "bg-red-100 text-red-700",
      };

    case "pending":
    default:
      return {
        label: "Pending",
        className: "bg-yellow-100 text-yellow-700",
      };
  }
}

RFQCard.Header = function () {
  const { quote } = useRFQCard();

  const { label, className } = getRFQStatusStyles(quote.status);

  return (
    <div className="flex justify-between items-start mb-2">
      <div className="flex items-center gap-2 text-gray-700">
        <MapPinIcon className="size-4 text-gray-500" />
        <h3 className="text-sm font-semibold">
          {quote.from_json?.name} → {quote.to_json?.name}
        </h3>
      </div>

      <span
        className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${className}`}
      >
        {label}
      </span>
    </div>
  );
};

RFQCard.SubInfo = function RFQCardSubInfo() {
  const { quote } = useRFQCard();

  const isOcean = quote.freight_type?.toLowerCase() === "ocean";
  const FreightIcon = isOcean ? ShipIcon : AirPlaneIcon;

  return (
    <div className="flex items-center gap-2 text-xs text-gray-500">
      <FreightIcon className="size-4 text-blue-500" />
      <span>{quote.freight_type?.toUpperCase() || "—"}</span>
      <span className="text-gray-400">|</span>
      <span>{quote.weight ? `${quote.weight} kg` : "—"}</span>
    </div>
  );
};

RFQCard.Meta = function RFQCardMeta({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
      {children}
    </div>
  );
};

RFQCard.Footer = function RFQCardFooter() {
  const { quote } = useRFQCard();

  const price = quote.ai_response?.quote?.estimated_price_usd;
  const transactionId = quote.transaction_id;

  return (
    <div className="mt-3 flex justify-between items-center">
      <p className="text-xs text-gray-500">
        ID: <span className="text-gray-700 font-medium">{transactionId}</span>
      </p>

      {price ? (
        <div className="flex items-baseline gap-1">
          <p className="text-sm font-semibold text-green-600">
            ${price.toLocaleString()}
          </p>
          <span className="text-[11px] text-gray-400">(Estimated)</span>
        </div>
      ) : (
        <p className="text-xs text-gray-400 italic">Awaiting Quote</p>
      )}
    </div>
  );
};

RFQCard.Actions = function RFQCardActions() {
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const { quote } = useRFQCard();
  const isPending = quote.status === "pending" || quote.status == null;

  const toggleForm = () => setShowAcceptForm((prev) => !prev);

  return (
    <>
      <hr className="my-3 border-gray-200" />

      {/* Admin Actions */}

      {!isPending || showAcceptForm ? (
        <RFQCard.AcceptForm onClose={toggleForm} />
      ) : (
        <div className="flex justify-end gap-2">
          <button
            onClick={toggleForm}
            className="px-3 py-1.5 text-xs font-medium rounded-md bg-green-100 text-green-700 hover:bg-green-200 transition"
          >
            Accept
          </button>

          <button className="px-3 py-1.5 text-xs font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition">
            Deny
          </button>
        </div>
      )}
    </>
  );
};

type AcceptQuoteForm = {
  quotes: {
    vendor: string;
    our_cost?: number;
    price?: number;
    expiry: string;
    quote_file?: FileList;
  }[];
};

RFQCard.AcceptForm = function RFQCardAcceptForm({
  onClose,
}: {
  onClose: () => void;
}) {
  const { quote } = useRFQCard();
  const actualQuotes = quote.actual_quotes || [];

  const [updateRFQs] = useUpdateRFQsMutation();
  const navigate = useNavigate();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<AcceptQuoteForm>({
    defaultValues: {
      quotes: [
        {
          vendor: "",
          our_cost: undefined,
          price: undefined,
          expiry: "",
        },
      ],
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "quotes",
  });

  useEffect(() => {
    if (actualQuotes?.length > 0) {
      replace(actualQuotes);
    }
  }, [actualQuotes, replace]);

  const onSubmit = async (values: AcceptQuoteForm) => {
    // console.log("Submit payload:", values);

    const payload = {
      uid: quote.uid,
      id: quote.id || quote.quotes_id,
      status: "active" as LogisticsRequestQuote["status"],
      actual_quotes: values.quotes,
    };

    const { data } = await updateRFQs(payload);

    if (data.status === "success") {
      toast.success("Quotes successfully added");
      navigate(`/admin/customer-profiles/${quote.uid}/quotes/${quote.id}`, {
        replace: true,
      });
    } else {
      toast.error("Something went wrong!");
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-3"
    >
      {fields.map((field, index) => (
        <div
          key={field.id}
          className="w-full flex flex-wrap gap-2 items-center"
        >
          {/* Vendor */}
          <input
            placeholder="Vendor name"
            className={`px-2 py-1.5 w-full sm:flex-[2] border rounded-md text-xs focus:outline-none focus:ring-1
                ${
                  errors.quotes?.[index]?.vendor
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-blue-500"
                }`}
            {...register(`quotes.${index}.vendor`, { required: true })}
          />

          {/* Cost */}
          <input
            type="number"
            placeholder="Our cost (USD)"
            className={`px-2 py-1.5 w-full sm:flex-1 border rounded-md text-xs focus:outline-none focus:ring-1
                ${
                  errors.quotes?.[index]?.our_cost
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-blue-500"
                }`}
            {...register(`quotes.${index}.our_cost`, {
              required: true,
              min: 1,
            })}
          />

          {/* Price */}
          <input
            type="number"
            placeholder="Sell price (USD)"
            className={`px-2 py-1.5 w-full sm:flex-1 border rounded-md text-xs focus:outline-none focus:ring-1
                ${
                  errors.quotes?.[index]?.price
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-blue-500"
                }`}
            {...register(`quotes.${index}.price`, { required: true, min: 1 })}
          />

          {/* Expiry */}
          <label
            className={`relative px-3 py-1.5 w-full sm:flex-1 text-xs rounded-md cursor-pointer bg-white
                ${
                  errors.quotes?.[index]?.expiry
                    ? "border border-red-500"
                    : "border border-gray-300"
                }`}
          >
            {watch(`quotes.${index}.expiry`) || (
              <span className="text-gray-400">Enter Expiry date</span>
            )}
            <input
              type="date"
              min={today}
              className="absolute inset-0 opacity-0 cursor-pointer"
              {...register(`quotes.${index}.expiry`, { required: true })}
              onClick={(e) => e.currentTarget.showPicker()}
            />
          </label>

          {/* Upload */}
          <label className="px-3 py-1.5 w-full sm:flex-1 text-xs border border-gray-300 rounded-md cursor-pointer bg-white text-gray-600">
            <span className="text-gray-400">Upload quote</span>
            <input
              type="file"
              className="hidden"
              {...register(`quotes.${index}.quote_file`)}
            />
          </label>

          {/* Remove */}
          {fields.length > 1 && (
            <button
              type="button"
              onClick={() => remove(index)}
              className="px-2 py-1 text-xs rounded-md bg-red-100 text-red-600 hover:bg-red-200"
            >
              ✕
            </button>
          )}
        </div>
      ))}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={() =>
            append({
              vendor: "",
              our_cost: undefined,
              price: undefined,
              expiry: "",
            })
          }
          className="text-xs font-medium text-blue-600 hover:underline"
        >
          + Add another quote
        </button>

        <div className="flex gap-2">
          <button
            type="submit"
            className="inline-flex items-center gap-1 px-4 py-2 text-xs font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300"
            disabled={isSubmitting}
          >
            {isSubmitting && (
              <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-custom-blue-medium" />
            )}
            Save
          </button>

          {quote.status === "pending" ||
            (!quote.status && (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-xs font-semibold rounded-md bg-gray-600 text-white hover:bg-gray-700 disabled:bg-gray-400"
                disabled={isSubmitting}
              >
                Close
              </button>
            ))}
        </div>
      </div>
    </form>
  );
};

RFQCard.ConfirmedQuote = function RFQCardConfirmedQuote() {
  const { quote } = useRFQCard();

  const confirmedQuote = quote.actual_quotes?.find(
    (q) => q.status?.toLowerCase() === "confirmed",
  );

  if (!confirmedQuote) return null;

  return (
    <div className="border-t border-gray-100 pt-6 mt-4">
      <h3 className="text-sm font-semibold text-gray-800 mb-4">
        Confirmed Vendor
      </h3>

      <div className="rounded-2xl border border-green-200 bg-green-50/60 p-5 shadow-sm">
        {/* Top Section */}
        <div className="flex justify-between items-start">
          <div className="flex items-start gap-3">
            {/* Success Icon */}
            <div className="bg-green-100 text-green-600 rounded-full p-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            {/* Vendor Info */}
            <div>
              <p className="text-base font-semibold text-gray-900">
                {confirmedQuote.vendor}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Expires: {confirmedQuote.expiry || "—"}
              </p>
            </div>
          </div>

          {/* Price */}
          <div className="text-right">
            <p className="text-xl font-bold text-green-600">
              ${confirmedQuote.price.toLocaleString()}
            </p>
            <p className="text-[11px] text-gray-400">Final Agreed Price</p>
          </div>
        </div>
      </div>
    </div>
  );
};

RFQCard.CompleteTransactionButton =
  function RFQCardCompleteTransactionButton() {
    const { quote } = useRFQCard();

    const navigate = useNavigate();
    const [completeQuote, { isLoading }] = useUpdateRFQsMutation();

    const handleComplete = async () => {
      const payload = {
        uid: quote.uid,
        id: quote.id || quote.quotes_id,
        status: "completed" as LogisticsRequestQuote["status"],
      };

      const { data } = await completeQuote(payload);

      if (data.status === "success") {
        toast.success("Transaction successfully completed 🎉");

        navigate(`/admin/customer-profiles/${quote.uid}/history/${quote.id}`, {
          replace: true,
        });
      } else {
        toast.error("Something went wrong!");
      }
    };

    return (
      <div className="mt-5 flex justify-end">
        <button
          type="button"
          disabled={isLoading}
          className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
          onClick={handleComplete}
        >
          {isLoading ? "Completing..." : "Mark Complete Transaction"}
        </button>
      </div>
    );
  };

RFQCard.CompletedQuote = function RFQCardCompletedQuote() {
  const { quote } = useRFQCard();

  if (quote.status !== "completed") return null;

  const completedQuote = quote.actual_quotes?.find(
    (q) => q.status === "confirmed",
  );

  if (!completedQuote) return null;

  return (
    <div className="border-t border-gray-100 pt-6 mt-4">
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-6 shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 text-emerald-600 rounded-full p-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Transaction Completed
              </h3>
              <p className="text-xs text-gray-500">
                This quote has been successfully finalized.
              </p>
            </div>
          </div>

          <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700">
            Completed
          </span>
        </div>

        {/* Details */}
        {completedQuote && (
          <div className="flex justify-between items-center border-t border-emerald-200 pt-4 mt-4">
            <div>
              <p className="text-sm font-medium text-gray-800">
                {completedQuote.vendor}
              </p>
              <p className="text-xs text-gray-500 mt-1">Final agreed vendor</p>
            </div>

            <div className="text-right">
              <p className="text-lg font-bold text-emerald-600">
                ${completedQuote.price?.toLocaleString()}
              </p>
              <p className="text-[11px] text-gray-400">
                Final transaction amount
              </p>
            </div>
          </div>
        )}

        {/* Footer message */}
        <div className="mt-5 text-sm text-gray-600">
          The transaction has been archived and is now available in your history
          records.
        </div>
      </div>
    </div>
  );
};

RFQCard.ProcurementAiReqOrder = function RFQCardProcurementAiReqOrder() {
  const { quote } = useRFQCard();
  const { label, className } = getRFQStatusStyles(quote.status);

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <MapPinIcon className="size-4 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-800">
              {quote?.procurement_location}
            </h2>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <BoxIcon className="size-4 text-blue-500" />
            <span>{quote?.goods_specifications}</span>
          </div>

          {quote.created_at && (
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <ClockIcon className="size-4 text-gray-300" />
              <span>{timeAgo(parseServerDate(quote.created_at))}</span>
            </div>
          )}
        </div>
        <span
          className={`text-xs px-2 py-1 rounded-full font-medium h-fit ${className}`}
        >
          {label}
        </span>
      </div>

      <hr />

      <div className="space-y-[10px] mt-3">
        {quote &&
          OrderRequestKeys.map(({ label, key }) => {
            const value = quote?.[key] || "";

            const isFilled =
              value !== null && value !== undefined && value !== "";

            return (
              <p key={key} className="flex items-center gap-2 text-sm">
                {isFilled ? (
                  <CheckIcon className="size-4 text-custom-blue-medium shrink-0" />
                ) : (
                  <CrossIcon className="size-4 flex items-center justify-center text-red-500 shrink-0" />
                )}

                <span className="text-[#475467]">
                  {label}: {String(value)}
                </span>
              </p>
            );
          })}
      </div>
    </div>
  );
};
